import FinishStep from "@/components/getting-started/FinishStep";
import ProfileAvatarSelector from "@/components/avatar/ProfileAvatarSelector";
import DetailsView from "@/components/resume/detail/DetailsView";
import { EducationTab } from "@/components/resume/education/EducationTab";
import EmploymentTab from "@/components/resume/employment/EmploymentTab";
import ResumeUpload from "@/components/resume/ResumeUpload";
import SkillsView from "@/components/resume/skill/SkillsView";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from '@/lib/api';
import { ChevronLeft, LogOut } from "lucide-react";
import React, { useState } from "react";
import logo from "../assets/hiredcoach-logo.svg";

const LogoutButtonInline: React.FC = () => {
  const { logout } = useAuth();
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={logout}
      className="h-8 w-8"
      title="Logout"
    >
      <LogOut className="h-5 w-5" />
    </Button>
  );
};

const GettingStarted: React.FC = () => {
  const [step, setStep] = useState<"welcome" | "path" | "upload" | "upload-complete" | "education" | "employment" | "about" | "finish">("welcome");
  const noBack = ["welcome", "path", "upload-complete"];
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { api } = useApiClient();

  return (
    <div className="min-h-screen relative flex flex-col justify-center items-center bg-gradient-to-br from-[#f7f9fb] via-[#eaf3fa] to-[#f2f5f9]">
      {/* Logout button top right */}
      <div className="absolute top-6 right-8 z-10">
        <LogoutButtonInline />
      </div>
      <main className="flex flex-1 items-center justify-center w-full">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6">
          <div className={`bg-white rounded-2xl shadow-2xl p-4 sm:p-6 lg:p-10 flex flex-col items-center border border-blue-50 ${step === 1 ? 'items-stretch' : ''}`}>
            <img src={logo} alt="HiredCoach Logo" className="w-36 mb-6 drop-shadow-lg mx-auto" />
            {(!noBack.includes(step))&& (
              <Button
                className="absolute top-6 left-6"
                variant="ghost"
                size="icon"
                onClick={() => {
                    const stepsMap = {
                      "upload": "path",
                      "education": "path",
                      "employment": "education",
                      "about": "employment",
                      "finish": "about",
                    }
                    setStep(stepsMap[step]);
                }}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            {step === "welcome" && (
              <>
                <h1 className="text-3xl font-extrabold mb-2 text-center text-gray-900">Welcome to HiredCoach!</h1>
                <p className="text-center mb-8 text-gray-600">We need some details to help you with your job search</p>
                <Button className="w-full text-lg py-3 rounded-lg" onClick={() => setStep("path")}>
                  Let's Go!
                </Button>
              </>
            )}
            {step === "path" && (
              <>
                <h2 className="text-2xl font-bold mb-8 text-center">How would you like to get started?</h2>
                <div className="flex flex-col gap-6 w-full max-w-2xl mx-auto">
                  <button
                    className="rounded-xl border-2 border-interview-blue bg-white shadow-md px-8 py-8 flex flex-col items-center transition hover:bg-interview-blue/10 focus:outline-none focus:ring-2 focus:ring-interview-blue"
                    onClick={() => setStep("upload")}
                  >
                    <span className="text-xl font-semibold text-interview-blue mb-2">Upload Your Resume</span>
                    <span className="text-gray-600">We'll extract your info automatically for you to review and edit.</span>
                  </button>
                  <button
                    className="rounded-xl border-2 border-interview-teal bg-white shadow-md px-8 py-8 flex flex-col items-center transition hover:bg-interview-teal/10 focus:outline-none focus:ring-2 focus:ring-interview-teal"
                    onClick={() => setStep("education")}
                  >
                    <span className="text-xl font-semibold text-interview-teal mb-2">Enter Manually</span>
                    <span className="text-gray-600">Start by entering your education, experience, and skills step by step.</span>
                  </button>
                </div>
              </>
            )}

            {step === "upload" && (
              <>
                <ResumeUpload
                  onUpload={() => { 
                    setStep("upload-complete")
                  }}
                />
              </>
            )}

            { step === "upload-complete" && (
              <>
                <h2 className="text-xl font-semibold mb-6 text-center">Got it</h2>
                <p className="text-center mb-6 text-gray-600">We've read your resume and filled out some stuff for you. Spot check the results and see if we missed anything.</p>
                <Button
                  onClick={() => setStep("education")}
                  className="w-full text-lg py-3 rounded-lg"
                >
                  Onward
                </Button>
              </>
            )}

            {step === "education" && (
              <>
                <EducationTab />
                <Button
                  onClick={() => setStep("employment")}
                  className="w-full text-base sm:text-lg py-3 rounded-lg mt-4 sm:mt-6"
                >
                  Employment
                </Button>
              </>
            )}

            {step === "employment" && (
              <>
                <EmploymentTab />
                <Button
                  onClick={() => setStep("about")}
                  className="w-full text-base sm:text-lg py-3 rounded-lg mt-4 sm:mt-6"
                >
                  About You
                </Button>
              </>
            )}

            {step === "about" && (
              <>
                <h2 className="text-2xl font-bold mb-6 text-center">About You</h2>
                <div className="w-full px-4 space-y-6">
                  <ProfileAvatarSelector />
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Key Skills</h3>
                    <p className="text-sm text-gray-600 mb-4">Add skills that are not specific to any job, like programming languages, software, or other relevant abilities.</p>
                    <SkillsView />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-3">General Achievements & Details</h3>
                    <p className="text-sm text-gray-600 mb-4">Include any other details or accomplishments you want to highlight that don't fit under a specific job or education entry.</p>
                    <DetailsView />
                  </div>
                </div>
                <Button
                  onClick={() => setStep("finish")}
                  className="w-full text-lg py-3 rounded-lg mt-8"
                >
                  Finish
                </Button>
              </>
            )}
            {step === "finish" && <FinishStep onBack={() => setStep("about")} />}
          </div>
        </div>
      </main>
    </div>
  );
};

export default GettingStarted;
